import type { ParseKeys } from 'i18next';
import type { components } from '@/generated/api/schema';
import type { OrganizationType } from '@/models/organizationType';

/**
 * The full closed RBAC vocabulary (E10-08, 11 values), taken straight from the generated wire
 * union rather than re-typed by hand — so {@link ROLE_DISPLAY} below is checked against the
 * contract itself and cannot drift from it.
 */
export type Role = components['schemas']['Role'];

/**
 * The i18n message keys {@link ROLE_DISPLAY} maps a {@link Role} to, scoped to the shared `role`
 * i18n namespace (`shared/i18n/role/de.json`) — consumers bind it with
 * `useTranslation('role')`. Mirrors `OrganizationStateI18nKey`'s scoping to `organizationState`
 * and `OrganizationTypeDisplay`'s to `organizationType`.
 */
export interface RoleDisplay {
  /** Key for the role's short display name, e.g. "Geschäftsführung". */
  readonly labelKey: ParseKeys<'role'>;
  /** Key for the one-sentence description shown below the name on the role radio-cards. */
  readonly descriptionKey: ParseKeys<'role'>;
}

/**
 * Maps each backend role name to its i18n message keys in the shared `role` namespace
 * (`shared/i18n/role/de.json`). Only the **keys** live here, never the German wording — that
 * stays single-sourced in the i18n resource file, exactly as `ORGANIZATION_STATE_DISPLAY`
 * (`models/organizationState.ts`) and `ORGANIZATION_TYPE_DISPLAY`
 * (`models/organizationType.ts`) handle their vocabularies.
 *
 * The wording lives in a shared namespace rather than under one feature, because it is read by
 * features that are otherwise unrelated: the Mitarbeiter-CreatePages, the customer detail page's
 * Benutzerkonten table, and the global Benutzerliste. Same move, and same reason, as the
 * `organizationState` namespace.
 *
 * <b>This mapping deliberately lives in the frontend.</b> `GET /api/v1/roles` used to ship an
 * `i18nKey` per role, which put the frontend's message catalogue into the API contract and tied
 * the enum constant's spelling to a translation key — a rename on either side would have broken
 * the other silently. The server now returns the vocabulary only; naming it is the client's job.
 *
 * Typed as `Record<Role, …>` against the **generated** union, so adding a twelfth role to the
 * OpenAPI contract fails the build here until it has been given labels — the same
 * exhaustiveness motive as the `UnclassifiedOrganizationType` guard in `models/organizationType.ts`.
 */
const ROLE_DISPLAY: Readonly<Record<Role, RoleDisplay>> = {
  PRACTICE_CEO: { labelKey: 'practice_ceo', descriptionKey: 'practice_ceo_desc' },
  PRACTICE_LEAD: { labelKey: 'practice_lead', descriptionKey: 'practice_lead_desc' },
  PRACTICE_EMPLOYEE: { labelKey: 'practice_employee', descriptionKey: 'practice_employee_desc' },
  CUSTOMER_LAB_CEO: { labelKey: 'customer_lab_ceo', descriptionKey: 'customer_lab_ceo_desc' },
  CUSTOMER_LAB_LEAD: { labelKey: 'customer_lab_lead', descriptionKey: 'customer_lab_lead_desc' },
  CUSTOMER_LAB_EMPLOYEE: {
    labelKey: 'customer_lab_employee',
    descriptionKey: 'customer_lab_employee_desc',
  },
  DELABO_LAB_CEO: { labelKey: 'delabo_lab_ceo', descriptionKey: 'delabo_lab_ceo_desc' },
  DELABO_LAB_LEAD: { labelKey: 'delabo_lab_lead', descriptionKey: 'delabo_lab_lead_desc' },
  DELABO_LAB_EMPLOYEE: {
    labelKey: 'delabo_lab_employee',
    descriptionKey: 'delabo_lab_employee_desc',
  },
  PORTAL_ADMIN: { labelKey: 'portal_admin', descriptionKey: 'portal_admin_desc' },
  PORTAL_SUPERUSER: { labelKey: 'portal_superuser', descriptionKey: 'portal_superuser_desc' },
};

/**
 * Looks up the i18n keys for a backend role name. Returns `null` for any value outside the closed
 * vocabulary — a role rolled out on the backend before this build knows about it, or a malformed
 * response — so callers render a fail-closed fallback (the raw role name) instead of a bare
 * `role:…` key leaking into the UI. Mirrors `organizationStateDisplay` and
 * `organizationTypeDisplay`.
 *
 * The single entry point for role display text: no feature may build its own role-to-label map,
 * and no caller may derive a key from the role name by string manipulation — a derived key has no
 * compile-time link to the resource file and silently resolves to nothing when either side is
 * renamed.
 */
export function roleDisplay(role: string | null | undefined): RoleDisplay | null {
  return typeof role === 'string' ? (ROLE_DISPLAY[role as Role] ?? null) : null;
}

/**
 * The roles bound to each {@link OrganizationType} — mirrors the backend's authoritative,
 * code-maintained `OrganizationType → Set<Role>` binding (`RoleModel.assignableRoles`) so a
 * role-*filter*'s options never drift from the vocabulary the backend actually enforces. One
 * disjoint role set per organization type, in the same CEO → LEAD → EMPLOYEE (or ADMIN →
 * SUPERUSER) order the backend declares them.
 *
 * <b>Not a substitute for `GET /api/v1/roles`.</b> That endpoint answers "which roles may *this
 * caller* hand out", which is strictly narrower and actor-dependent (a `PRACTICE_CEO` may assign
 * only `PRACTICE_EMPLOYEE`) — use it, via `useAssignableRoles`, wherever a role is being
 * *assigned*. This constant answers the different, actor-independent question "which roles can
 * exist within this organization type", which is what a filter dropdown needs and what no
 * endpoint exposes. Widening a filter never widens what the server returns: visibility is
 * enforced server-side regardless of what the client asks to filter by.
 */
const ROLES_BY_ORGANIZATION_TYPE = {
  PRACTICE: ['PRACTICE_CEO', 'PRACTICE_LEAD', 'PRACTICE_EMPLOYEE'],
  CUSTOMER_LAB: ['CUSTOMER_LAB_CEO', 'CUSTOMER_LAB_LEAD', 'CUSTOMER_LAB_EMPLOYEE'],
  DELABO_LAB: ['DELABO_LAB_CEO', 'DELABO_LAB_LEAD', 'DELABO_LAB_EMPLOYEE'],
  DELABO_PORTAL: ['PORTAL_ADMIN', 'PORTAL_SUPERUSER'],
} as const satisfies Readonly<Record<OrganizationType, readonly Role[]>>;

/**
 * Compile-time exhaustiveness pin: every {@link Role} in the generated contract must be bound to
 * exactly one organization type above. Without it, a twelfth role added to the OpenAPI enum would
 * get labels (`ROLE_DISPLAY` is a `Record<Role, …>` and fails the build) but would silently never
 * appear in the unrestricted role filter — present in the vocabulary, unreachable in the UI.
 * Mirrors the `UnclassifiedOrganizationType` guard in `models/organizationType.ts`.
 */
type RoleNotBoundToAnOrganizationType = Exclude<
  Role,
  (typeof ROLES_BY_ORGANIZATION_TYPE)[OrganizationType][number]
>;
const _everyRoleIsBoundToAnOrganizationType: RoleNotBoundToAnOrganizationType extends never
  ? true
  : never = true;
void _everyRoleIsBoundToAnOrganizationType;

/**
 * All 11 roles, in canonical (backend enum) order — the unrestricted filter option set for the
 * portal audience, which may filter by any role regardless of organization type. Derived from
 * {@link ROLES_BY_ORGANIZATION_TYPE} rather than declared a second time, so the two can never
 * drift apart.
 */
export const ALL_ROLES: readonly Role[] = Object.values(ROLES_BY_ORGANIZATION_TYPE).flat();

/**
 * Returns the roles that can exist within an organization type (audience-relevant filter options
 * for an organization-scoped view), e.g. `PRACTICE` → the three practice roles. Fail-closed: an
 * unrecognized or absent organization type yields an empty array rather than a guess, since this
 * also gates which roles a caller may filter by.
 */
export function rolesForOrganizationType(orgType: string | null | undefined): readonly Role[] {
  if (orgType == null) return [];
  return ROLES_BY_ORGANIZATION_TYPE[orgType as OrganizationType] ?? [];
}

/** One organization type together with the roles bound to it — see {@link ROLE_GROUPS}. */
export interface RoleGroup {
  readonly organizationType: OrganizationType;
  readonly roles: readonly Role[];
}

/**
 * {@link ALL_ROLES} partitioned by organization type, in the same canonical order — the option
 * structure for an *unrestricted* role picker.
 *
 * Exists because the three CEO/LEAD/EMPLOYEE tiers share their labels across organization types
 * ("Geschäftsführung" is the label for `PRACTICE_CEO`, `CUSTOMER_LAB_CEO` and `DELABO_LAB_CEO`
 * alike). A flat unrestricted list therefore renders three visually identical entries per tier,
 * indistinguishable to the user even though the underlying values differ. Grouping by
 * organization type restores the distinction without lengthening every label.
 *
 * Derived from {@link ROLES_BY_ORGANIZATION_TYPE}, like {@link ALL_ROLES}, so the flat and grouped
 * views can never disagree about which roles exist or in what order.
 */
export const ROLE_GROUPS: readonly RoleGroup[] = Object.entries(ROLES_BY_ORGANIZATION_TYPE).map(
  ([organizationType, roles]) => ({ organizationType: organizationType as OrganizationType, roles }),
);

/**
 * {@link ROLE_GROUPS} narrowed to the given organization types, in the same canonical group order
 * — the option structure for a picker whose audience sees **several** organization types but not
 * all of them.
 *
 * The DELABO-lab Benutzerliste is that case, and it is why this exists: a lab sees its own users
 * plus those of its assigned customers, so answering "which roles may this audience filter by"
 * from the caller's own organization type alone (as {@link rolesForOrganizationType} does) offered
 * the three lab roles only. Filtering by "Geschäftsführung" then sent `DELABO_LAB_CEO` and
 * silently returned nothing for a customer's GF — visible in the list, unselectable in the filter.
 *
 * Returns groups rather than a flat role list on purpose: the CEO/LEAD/EMPLOYEE labels repeat
 * across organization types, so any multi-type option set needs the group heading to tell the
 * copies apart, for the same reason {@link ROLE_GROUPS} does. Unknown types are dropped rather
 * than guessed, and a type with no roles yields no group — the caller renders whatever remains.
 *
 * Derived from {@link ROLE_GROUPS} by filtering, never by re-listing roles, so it cannot disagree
 * with the flat and unrestricted views about which roles exist or in what order.
 */
export function roleGroupsForOrganizationTypes(
  orgTypes: readonly OrganizationType[],
): readonly RoleGroup[] {
  return ROLE_GROUPS.filter(
    (group) => orgTypes.includes(group.organizationType) && group.roles.length > 0,
  );
}

/**
 * Three-tier RBAC hierarchy (CEO > LEAD > EMPLOYEE).
 *
 * The tier names deliberately mirror the suffixes of the backend role names (E10-08 11-role
 * model) rather than introducing a parallel vocabulary — `PRACTICE_LEAD` maps to `LEAD`, not to
 * a synonym. A rename on either side must stay a rename on both.
 */
export type RoleTier = 'CEO' | 'LEAD' | 'EMPLOYEE';

/**
 * Derives the coarse {@link RoleTier} from a backend role name (E10-08 11-role model), e.g.
 * `DELABO_LAB_CEO` → `CEO`, `PRACTICE_LEAD` → `LEAD`, `CUSTOMER_LAB_EMPLOYEE` → `EMPLOYEE`.
 * Returns null for roles without a tier (e.g. `PORTAL_ADMIN`/`PORTAL_SUPERUSER`).
 */
export function tierFromRole(role: string | null | undefined): RoleTier | null {
  if (typeof role !== 'string') return null;
  if (role.endsWith('_CEO')) return 'CEO';
  if (role.endsWith('_LEAD')) return 'LEAD';
  if (role.endsWith('_EMPLOYEE')) return 'EMPLOYEE';
  return null;
}

/**
 * Single source of truth for which backend role names may access the support dashboard —
 * its route guard, its portal-nav entry, and the post-login redirect all read from this
 * constant, never from an ad-hoc copy (issue #356).
 *
 * Contains both `DELABO_PORTAL` roles: `'PORTAL_ADMIN'` and `'PORTAL_SUPERUSER'` — a
 * Portal-Superuser can do strictly everything a Portal-Admin can (E10-08), so it is never
 * excluded from anything Portal-Admin is allowed to reach.
 */
export const SUPPORT_DASHBOARD_ALLOWED_ROLES: readonly string[] = ['PORTAL_ADMIN', 'PORTAL_SUPERUSER'];

/**
 * Resolves the landing route for an authenticated principal based on their backend role name,
 * reading from the same {@link SUPPORT_DASHBOARD_ALLOWED_ROLES} single source of truth as the
 * route guard and the portal-nav entry (issue #356). Support roles land on `/support-dashboard`;
 * every other role (including unknown/unmapped ones) falls back to the regular dashboard `/`.
 *
 * Intended as the single place that decides "where does this user end up after login / on an
 * unmatched route", replacing ad-hoc redirect checks scattered across route components.
 */
export function resolveHomeRouteForRole(role: string | null | undefined): string {
  if (typeof role === 'string' && SUPPORT_DASHBOARD_ALLOWED_ROLES.includes(role)) {
    return '/support-dashboard';
  }
  return '/';
}
